import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import './Tasks.css';

interface Task {
  _id: string;
  title: string;
  description?: string;
  dueDate?: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'completed';
  assignedTo?: {
    userType: 'admin' | 'guest';
    userId: string;
    userName: string;
  };
  createdAt: string;
}

interface Guest {
  _id: string;
  name: string;
}

const Tasks: React.FC = () => {
  const { accessToken, logout, loading: authLoading } = useAuth();
  const history = useHistory();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [familySide, setFamilySide] = useState<'groom' | 'bride' | ''>('');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    assignedToType: 'admin' as 'admin' | 'guest',
    assignedToId: '',
  });

  const fetchData = async () => {
    try {
      // Fetch Profile
      const profileRes = await fetch(`${API_BASE_URL}/api/admin/profile`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const profileData = await profileRes.json();
      if (profileRes.ok) {
        setFamilySide(profileData.admin.familySide);
        setFormData(prev => ({ ...prev, assignedToId: profileData.admin._id }));
      }

      // Fetch Tasks
      const tasksRes = await fetch(`${API_BASE_URL}/api/tasks`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const tasksData = await tasksRes.json();
      if (tasksRes.ok) {
        setTasks(tasksData.tasks);
      }

      // Fetch Guests (for assignment)
      const guestsRes = await fetch(`${API_BASE_URL}/api/guests`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const guestsData = await guestsRes.json();
      if (guestsRes.ok) {
        setGuests(guestsData.guests.filter((g: any) => g.status === 'approved'));
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      if (accessToken) {
        fetchData();
      } else {
        history.push('/login');
      }
    }
  }, [accessToken, authLoading, history]);

  const handleEditTaskClick = (task: Task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
      priority: task.priority,
      assignedToType: task.assignedTo?.userType || 'admin',
      assignedToId: task.assignedTo?.userId || '',
    });
    setIsModalOpen(true);
  };

  const handleSubmitTask = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let assignedName = 'Admin';
      if (formData.assignedToType === 'admin') {
        assignedName = 'Self (Admin)';
      } else {
        const guest = guests.find(g => g._id === formData.assignedToId);
        assignedName = guest ? guest.name : 'Unknown';
      }

      const payload = {
        title: formData.title,
        description: formData.description,
        dueDate: formData.dueDate,
        priority: formData.priority,
        assignedTo: {
          userType: formData.assignedToType,
          userId: formData.assignedToId,
          userName: assignedName,
        }
      };

      const url = editingTask
        ? `${API_BASE_URL}/api/tasks/${editingTask._id}`
        : `${API_BASE_URL}/api/tasks`;
      const method = editingTask ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setIsModalOpen(false);
        setEditingTask(null);
        setFormData({
          title: '',
          description: '',
          dueDate: '',
          priority: 'medium',
          assignedToType: 'admin',
          assignedToId: '',
        });
        fetchData();
      } else {
        alert(editingTask ? 'Failed to update task' : 'Failed to create task');
      }
    } catch (err) {
      alert(editingTask ? 'Error updating task' : 'Error creating task');
    }
  };

  const toggleStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'pending' ? 'completed' : 'pending';
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        fetchData();
      }
    } catch (err) {
      alert('Error updating task');
    }
  };

  const deleteTask = async (id: string) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        fetchData();
      }
    } catch (err) {
      alert('Error deleting task');
    }
  };

  if (loading) return <div className="tasks-container">Loading...</div>;

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  return (
    <div className={`admin-dashboard tasks-container ${familySide ? `theme-${familySide}` : ''}`}>
      <div className="tasks-header">
        <div className="title-area">
          <h1>Task Management</h1>
          <p>Organize your {familySide === 'groom' ? "Groom's" : "Bride's"} side tasks and assignments</p>
        </div>
        <button className="add-task-btn" onClick={() => setIsModalOpen(true)}>
          + New Task
        </button>
      </div>

      <div className="tasks-stats-row">
        <div className="task-stat-card pending">
          <span className="count">{pendingTasks.length}</span>
          <span className="label">Tasks Pending</span>
        </div>
        <div className="task-stat-card completed">
          <span className="count">{completedTasks.length}</span>
          <span className="label">Tasks Completed</span>
        </div>
      </div>

      <div className="tasks-sections">
        <section className="task-list-section">
          <h2>Pending Tasks</h2>
          <div className="tasks-grid">
            {pendingTasks.length === 0 ? (
              <p className="no-tasks">No pending tasks. Great job!</p>
            ) : (
              pendingTasks.map(task => (
                <div key={task._id} className={`task-card priority-${task.priority}`}>
                  <div className="task-header">
                    <button className="status-toggle" onClick={() => toggleStatus(task._id, task.status)}>
                      ⭕
                    </button>
                    <h3>{task.title}</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="edit-btn" onClick={() => handleEditTaskClick(task)}>✏️</button>
                      <button className="delete-btn" onClick={() => deleteTask(task._id)}>🗑️</button>
                    </div>
                  </div>
                  <p className="task-desc">{task.description}</p>
                  <div className="task-footer">
                    <span className="assignee">👤 {task.assignedTo?.userName || 'Unassigned'}</span>
                    {task.dueDate && <span className="due-date">📅 {new Date(task.dueDate).toLocaleDateString()}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="task-list-section">
          <h2>Completed Tasks</h2>
          <div className="tasks-grid">
            {completedTasks.length === 0 ? (
              <p className="no-tasks">No completed tasks yet.</p>
            ) : (
              completedTasks.map(task => (
                <div key={task._id} className="task-card completed">
                  <div className="task-header">
                    <button className="status-toggle" onClick={() => toggleStatus(task._id, task.status)}>
                      ✅
                    </button>
                    <h3>{task.title}</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="edit-btn" onClick={() => handleEditTaskClick(task)}>✏️</button>
                      <button className="delete-btn" onClick={() => deleteTask(task._id)}>🗑️</button>
                    </div>
                  </div>
                  <div className="task-footer">
                    <span className="assignee">👤 {task.assignedTo?.userName}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editingTask ? 'Edit Task' : 'Create New Task'}</h2>
            <form onSubmit={handleSubmitTask}>
              <div className="form-group">
                <label>Task Title</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. Confirm Catering Menu"
                />
              </div>
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={formData.dueDate}
                    onChange={e => setFormData({ ...formData, dueDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={formData.priority}
                    onChange={e => setFormData({ ...formData, priority: e.target.value as any })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Assign To</label>
                <select
                  value={formData.assignedToType}
                  onChange={e => setFormData({ ...formData, assignedToType: e.target.value as any, assignedToId: '' })}
                >
                  <option value="admin">Assign to Myself (Admin)</option>
                  <option value="guest">Assign to Guest</option>
                </select>
              </div>
              {formData.assignedToType === 'guest' && (
                <div className="form-group">
                  <label>Select Guest</label>
                  <select
                    required
                    value={formData.assignedToId}
                    onChange={e => setFormData({ ...formData, assignedToId: e.target.value })}
                  >
                    <option value="">Choose a guest...</option>
                    {guests.map(guest => (
                      <option key={guest._id} value={guest._id}>{guest.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => {
                  setIsModalOpen(false);
                  setEditingTask(null);
                  setFormData({
                    title: '',
                    description: '',
                    dueDate: '',
                    priority: 'medium',
                    assignedToType: 'admin',
                    assignedToId: '',
                  });
                }}>Cancel</button>
                <button type="submit" className="submit-btn">{editingTask ? 'Save Changes' : 'Create Task'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;

